// ============================================================================
//  Tickets Module — Phase 5
//  Pages : Liste tickets, Détail, Dashboard SLA
// ============================================================================

import { ListPageSkeleton } from '@/components/ui/Skeleton'
import React, { useState } from 'react'
import { usersApi } from '@/modules/users/api/users.api'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Ticket as TicketIcon,
  Plus,
  Search,
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  MessageSquare,
  Paperclip,
  User,
  Users,
  Tag,
  ArrowUpRight,
  RefreshCw,
  X,
  Check,
  AlertTriangle,
  Circle,
  Pause,
  BarChart3,
  Edit2,
} from 'lucide-react'
import { ticketsApi, type Ticket, type TicketComment } from '@/modules/tickets/api/tickets.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'
import {
  SubTicketsPanel,
  LinkedTicketsPanel,
  MergeTicketButton,
  SatisfactionWidget,
} from './TicketsExtComponents'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('fr-SN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—'
const fmtTime = (d?: string) =>
  d ? new Date(d).toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit' }) : '—'
const fmtRelTime = (d: string) => {
  const diff = Date.now() - new Date(d).getTime()
  if (diff < 60000) return "À l'instant"
  if (diff < 3600000) return `${Math.floor(diff / 60000)}min`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`
  return `${Math.floor(diff / 86400000)}j`
}
const fmtDuration = (ms: number) => {
  if (ms <= 0) return 'Expiré'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}min` : `${m}min`
}

const PRIORITY_CONFIG: Record<
  string,
  { label: string; color: string; bg: string; border: string }
> = {
  CRITICAL: {
    label: 'Critique',
    color: 'text-[#DC2626]',
    bg: 'bg-[#F87171]/10',
    border: 'border-red-400/30',
  },
  HIGH: {
    label: 'Haute',
    color: 'text-[#EA580C]',
    bg: 'bg-[#F97316]/10',
    border: 'border-amber-400/30',
  },
  MEDIUM: {
    label: 'Moyenne',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    border: 'border-yellow-400/30',
  },
  LOW: {
    label: 'Faible',
    color: 'text-[var(--tx3)]',
    bg: 'bg-[var(--tx3)]/10',
    border: 'border-[var(--tx3)]/20',
  },
}

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: typeof Circle; color: string; bg: string }
> = {
  OPEN: { label: 'Ouvert', icon: Circle, color: 'text-[var(--blue-d)]', bg: 'bg-[#38BDF8]/10' },
  IN_PROGRESS: {
    label: 'En cours',
    icon: RefreshCw,
    color: 'text-[#D97706]',
    bg: 'bg-[#F59E0B]/10',
  },
  WAITING_INFO: {
    label: 'En attente',
    icon: Pause,
    color: 'text-[#EA580C]',
    bg: 'bg-[#F97316]/10',
  },
  IN_TEST: {
    label: 'En test',
    icon: CheckCircle,
    color: 'text-[#7C3AED]',
    bg: 'bg-purple-400/10',
  },
  RESOLVED: {
    label: 'Résolu',
    icon: CheckCircle,
    color: 'text-[#059669]',
    bg: 'bg-[#10B981]/10',
  },
  CLOSED: { label: 'Fermé', icon: XCircle, color: 'text-[var(--tx3)]', bg: 'bg-[#D6D2EC]/10' },
  CANCELLED: { label: 'Annulé', icon: XCircle, color: 'text-[var(--tx3)]', bg: 'bg-[var(--bd)]/10' },
}

const SLA_STATUS_COLOR: Record<string, string> = {
  OK: 'text-[#059669]',
  WARNING: 'text-[#D97706]',
  VIOLATED: 'text-[#DC2626]',
  MET: 'text-[var(--tx3)]',
}

const Spinner = () => (
  <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
)

function Avatar({ user, size = 'sm' }: { user?: any; size?: 'xs' | 'sm' }) {
  if (!user) return <div className="w-8 h-8 rounded-full bg-[var(--surface-2)]" />
  const s = { xs: 'w-6 h-6 text-[10px]', sm: 'w-8 h-8 text-xs' }[size]
  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`
  return user.avatarUrl ? (
    <img
      src={user.avatarUrl}
      alt={initials}
      className={`${s} rounded-full object-cover shrink-0`}
    />
  ) : (
    <div
      className={`${s} rounded-full bg-gradient-to-br from-primary-500 to-[var(--brand-amber)] flex items-center justify-center text-[var(--brand-dark)] font-bold shrink-0`}
    >
      {initials}
    </div>
  )
}

// ============================================================================
//  DASHBOARD TICKETS
// ============================================================================

export default function TicketsDashboard() {
  const navigate = useNavigate()
  const { data: dash, isLoading: dashLoading } = useQuery({
    queryKey: ['tickets-dashboard'],
    queryFn: ticketsApi.getDashboard,
  })
  const { data: sla } = useQuery({
    queryKey: ['tickets-sla'],
    queryFn: ticketsApi.getSlaStats,
  })

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Support Tickets</h1>
          <p className="text-[var(--tx3)] text-sm mt-0.5">Gestion des incidents et demandes</p>
        </div>
        <button
          onClick={() => navigate('/tickets/new')}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Nouveau ticket
        </button>
      </div>

      {/* KPIs SLA */}
      {sla && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
          {[
            {
              label: 'Total tickets',
              value: sla.total,
              icon: TicketIcon,
              color: 'text-[var(--blue-d)]',
              bg: 'bg-[#38BDF8]/10',
            },
            {
              label: 'Résolus',
              value: sla.resolved,
              icon: CheckCircle,
              color: 'text-[#059669]',
              bg: 'bg-[#10B981]/10',
            },
            {
              label: 'Violations SLA',
              value: sla.slaViolations ?? 0,
              icon: AlertCircle,
              color: 'text-[#DC2626]',
              bg: 'bg-[#F87171]/10',
            },
            {
              label: 'Conformité SLA',
              value: `${sla.slaComplianceRate}%`,
              icon: BarChart3,
              color: 'text-[#7C3AED]',
              bg: 'bg-purple-400/10',
              isText: true,
            },
          ].map((k) => (
            <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[var(--tx3)]">{k.label}</span>
                <div className={`p-1.5 ${k.bg} rounded-lg`}>
                  <k.icon className={`w-3.5 h-3.5 ${k.color}`} />
                </div>
              </div>
              <p className={`font-bold text-[var(--brand-dark)] ${k.isText ? 'text-xl' : 'text-2xl'}`}>
                {k.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Par statut */}
      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3">Par statut</h3>
            <div className="space-y-2">
              {dash.byStatus?.map((s: any) => {
                const cfg = STATUS_CONFIG[s.status]
                if (!cfg) return null
                const Icon = cfg.icon
                return (
                  <div key={s.status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                      <span className="text-sm text-[var(--tx2)]">{cfg.label}</span>
                    </div>
                    <span className={`text-sm font-bold ${cfg.color}`}>{s.count}</span>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3">Tickets récents</h3>
            <div className="space-y-2">
              {dash.recentTickets?.slice(0, 4).map((t: Ticket) => {
                const pCfg = PRIORITY_CONFIG[t.priority]
                return (
                  <button
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--surface-2)] transition-colors text-left"
                  >
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${pCfg?.bg} ${pCfg?.color} ${pCfg?.border} shrink-0`}
                    >
                      {pCfg?.label}
                    </span>
                    <span className="text-sm text-[var(--brand-dark)] truncate flex-1">{t.title}</span>
                    <span className="text-[10px] text-[var(--tx3)]">{fmtRelTime(t.createdAt)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  LISTE TICKETS
// ============================================================================

export function TicketsListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatus] = useState('')
  const [priorityFilter, setPriority] = useState('')
  const [myTickets, setMyTickets] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', search, statusFilter, priorityFilter, myTickets],
    queryFn: () =>
      ticketsApi.listTickets({
        search: search || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        myTickets: myTickets || undefined,
        limit: 30,
      }),
  })

  const tickets: Ticket[] = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Tickets</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filtres */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] w-48 focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] focus:outline-none"
          >
            <option value="">Tous statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriority(e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--bd)] rounded-xl text-sm text-[var(--brand-dark)] focus:outline-none"
          >
            <option value="">Toutes priorités</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setMyTickets((v) => !v)}
            className={`px-3 py-2 rounded-xl text-sm transition-colors ${myTickets ? 'bg-[var(--blue)] text-white' : 'bg-white border border-[var(--bd)] text-[var(--tx3)]'}`}
          >
            Mes tickets
          </button>
          <button
            onClick={() => navigate('/tickets/new')}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Ticket
          </button>
        </div>
      </div>

      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Référence', 'Titre', 'Priorité', 'Statut', 'Assigné à', 'SLA', 'Créé'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {tickets.map((t) => {
                const pCfg = PRIORITY_CONFIG[t.priority]
                const sCfg = STATUS_CONFIG[t.status]
                const SIcon = sCfg?.icon ?? Circle
                const slaResColor = t.slaInfo
                  ? SLA_STATUS_COLOR[t.slaInfo.resolutionStatus]
                  : 'text-[var(--tx3)]'
                return (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tickets/${t.id}`)}
                    className="hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-[var(--blue-d)]">#{t.number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-[var(--brand-dark)] line-clamp-1">{t.title}</p>
                      {t.category && (
                        <p className="text-[10px] text-[var(--tx3)]">{t.category.name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${pCfg?.bg} ${pCfg?.color} ${pCfg?.border}`}
                      >
                        {pCfg?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${sCfg?.bg} ${sCfg?.color}`}
                      >
                        <SIcon className="w-3 h-3" /> {sCfg?.label}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {t.assignee ? (
                        <div className="flex items-center gap-2">
                          <Avatar user={t.assignee} size="xs" />
                          <span className="text-xs text-[var(--tx2)]">{t.assignee.firstName}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--tx3)]">Non assigné</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {t.slaInfo &&
                        t.slaInfo.resolutionStatus !== 'MET' &&
                        t.slaInfo.resolutionRemainingMs !== null && (
                          <div className={`flex items-center gap-1 text-xs ${slaResColor}`}>
                            <Clock className="w-3 h-3" />
                            {t.slaInfo.resolutionStatus === 'VIOLATED'
                              ? 'Expiré'
                              : fmtDuration(t.slaInfo.resolutionRemainingMs!)}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--tx3)]">{fmtRelTime(t.createdAt)}</td>
                  </tr>
                )
              })}
              {tickets.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-[var(--tx3)] text-sm">
                    Aucun ticket
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  DÉTAIL TICKET
// ============================================================================

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotif = useNotificationStore((s) => s.addNotification)
  const { user, hasPermission } = useAuthStore()
  const [comment, setComment] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [showAssign, setShowAssign] = useState(false)
  const { data: agentUsersRaw } = useQuery({
    queryKey: ['ticket-agents'],
    queryFn: () => usersApi.directory({ limit: 100 }),
  })
  const agentUsers: any[] = Array.isArray(agentUsersRaw) ? agentUsersRaw : []

  const assignMutation = useMutation({
    mutationFn: (assigneeId: string) => ticketsApi.assignTicket(id!, { assignedToId: assigneeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] })
      setShowAssign(false)
      addNotif({ type: 'success', title: 'Ticket assigné' })
    },
    onError: (e: any) => addNotif({ type: 'error', title: 'Erreur', message: e.response?.data?.message }),
  })
  const [resolution, setResolution] = useState('')
  const [showResolve, setShowResolve] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({ title: '', priority: '', description: '' })

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.getTicket(id!),
    enabled: !!id,
  })

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['ticket', id] })

  const commentMutation = useMutation({
    mutationFn: () => ticketsApi.addComment(id!, { content: comment, isInternal }),
    onSuccess: () => {
      setComment('')
      refetch()
    },
    onError: (err: any) =>
      addNotif({
        type: 'error',
        title: 'Commentaire non envoyé',
        message: err?.response?.data?.message ?? 'Erreur inattendue',
      }),
  })

  const resolveMutation = useMutation({
    mutationFn: () => ticketsApi.resolveTicket(id!, { resolution }),
    onSuccess: () => {
      setShowResolve(false)
      refetch()
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Ticket résolu' })
    },
  })

  const closeMutation = useMutation({
    mutationFn: () => ticketsApi.closeTicket(id!),
    onSuccess: () => {
      refetch()
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Ticket fermé' })
    },
  })

  const reopenMutation = useMutation({
    mutationFn: () => ticketsApi.reopenTicket(id!),
    onSuccess: () => {
      refetch()
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Ticket rouvert' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: () => ticketsApi.updateTicket(id!, {
      title:       editForm.title.trim() || undefined,
      priority:    editForm.priority || undefined,
      description: editForm.description.trim() || undefined,
    }),
    onSuccess: () => {
      refetch()
      setIsEditing(false)
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Ticket mis à jour' })
    },
    onError: (e: any) => addNotif({ type: 'error', title: 'Erreur', message: e.response?.data?.message }),
  })

  const startEdit = () => {
    setEditForm({
      title:       ticket?.title ?? '',
      priority:    ticket?.priority ?? '',
      description: ticket?.description ?? '',
    })
    setIsEditing(true)
  }

  if (isLoading) return <ListPageSkeleton />
  if (!ticket) return <div className="text-center py-12 text-[var(--tx3)]">Ticket introuvable</div>

  const pCfg = PRIORITY_CONFIG[ticket.priority]
  const sCfg = STATUS_CONFIG[ticket.status]
  const SIcon = sCfg?.icon ?? Circle
  const comments: TicketComment[] = ticket.comments ?? []
  const isAgent = hasPermission('tickets:update')
  const canResolve = hasPermission('tickets:resolve')
  const canAssign = hasPermission('tickets:assign')

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-mono text-[var(--blue-d)]">#{ticket.number}</span>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${sCfg?.bg} ${sCfg?.color}`}
            >
              <SIcon className="w-3.5 h-3.5" /> {sCfg?.label}
            </span>
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${pCfg?.bg} ${pCfg?.color} ${pCfg?.border}`}
            >
              {pCfg?.label}
            </span>
          </div>
          {isEditing ? (
            <div className="space-y-3 mt-2 max-w-xl">
              <input
                value={editForm.title}
                onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                className="w-full text-xl font-bold text-[var(--brand-dark)] bg-[var(--surface-2)] border border-[var(--blue)]/40 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--tx3)] mb-1 block">Priorité</label>
                  <select
                    value={editForm.priority}
                    onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none"
                  >
                    {['LOW','MEDIUM','HIGH','URGENT'].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--tx3)] mb-1 block">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)] resize-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <Check className="w-3.5 h-3.5" /> Sauvegarder
                </button>
                <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 border border-[var(--bd)] rounded-lg text-sm text-[var(--tx3)]">
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <h1 className="text-xl font-bold text-[var(--brand-dark)]">{ticket.title}</h1>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isEditing && hasPermission('tickets:update') && !['RESOLVED','CLOSED'].includes(ticket.status) && (
            <button
              onClick={startEdit}
              className="p-2 hover:bg-[var(--bd)] rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors"
              title="Modifier"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {canResolve && ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' && (
            <button
              onClick={() => setShowResolve(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-xl text-sm"
            >
              <CheckCircle className="w-4 h-4" /> Résoudre
            </button>
          )}
          {ticket.status === 'RESOLVED' && canResolve && (
            <>
              <button
                onClick={() => closeMutation.mutate()}
                className="px-4 py-2 bg-[var(--surface-2)] hover:bg-[var(--bd)] text-[var(--brand-dark)] rounded-xl text-sm"
              >
                Fermer
              </button>
              <MergeTicketButton ticketId={id!} canMerge={canResolve} />
            </>
          )}
          {(ticket.status === 'RESOLVED' || ticket.status === 'CLOSED') && (
            <button
              onClick={() => reopenMutation.mutate()}
              className="px-4 py-2 bg-[var(--blue)]/20 text-[var(--blue-d)] rounded-xl text-sm hover:bg-[var(--blue)]/30"
            >
              Rouvrir
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Contenu principal */}
        <div className="lg:col-span-2 space-y-4">
          {/* Description */}
          <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3">Description</h3>
            <p className="text-sm text-[var(--tx2)] whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Détails du signalement (saisis à la création) */}
          {(ticket.applicationName ||
            ticket.environment ||
            ticket.stepsToReproduce ||
            ticket.expectedBehavior ||
            ticket.actualBehavior) && (
            <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-semibold text-[var(--brand-dark)]">Détails du signalement</h3>
              {(ticket.applicationName || ticket.environment) && (
                <div className="grid grid-cols-2 gap-4">
                  {ticket.applicationName && (
                    <div>
                      <p className="text-xs text-[var(--tx3)] mb-1">Application concernée</p>
                      <p className="text-sm text-[var(--tx2)]">{ticket.applicationName}</p>
                    </div>
                  )}
                  {ticket.environment && (
                    <div>
                      <p className="text-xs text-[var(--tx3)] mb-1">Environnement</p>
                      <p className="text-sm text-[var(--tx2)]">{ticket.environment}</p>
                    </div>
                  )}
                </div>
              )}
              {ticket.stepsToReproduce && (
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Étapes pour reproduire</p>
                  <p className="text-sm text-[var(--tx2)] whitespace-pre-wrap">{ticket.stepsToReproduce}</p>
                </div>
              )}
              {(ticket.expectedBehavior || ticket.actualBehavior) && (
                <div className="grid grid-cols-2 gap-4">
                  {ticket.expectedBehavior && (
                    <div>
                      <p className="text-xs text-[var(--tx3)] mb-1">Comportement attendu</p>
                      <p className="text-sm text-[var(--tx2)] whitespace-pre-wrap">{ticket.expectedBehavior}</p>
                    </div>
                  )}
                  {ticket.actualBehavior && (
                    <div>
                      <p className="text-xs text-[var(--tx3)] mb-1">Comportement observé</p>
                      <p className="text-sm text-[var(--tx2)] whitespace-pre-wrap">{ticket.actualBehavior}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* SLA */}
          {ticket.slaInfo && (
            <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> SLA
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Première réponse</p>
                  <p
                    className={`text-sm font-medium ${SLA_STATUS_COLOR[ticket.slaInfo.firstResponseStatus]}`}
                  >
                    {ticket.slaInfo.firstResponseStatus === 'MET'
                      ? '✓ Respecté'
                      : ticket.slaInfo.firstResponseStatus === 'VIOLATED'
                        ? '✗ Expiré'
                        : ticket.slaInfo.firstResponseRemainingMs != null
                          ? fmtDuration(ticket.slaInfo.firstResponseRemainingMs)
                          : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Résolution</p>
                  <p
                    className={`text-sm font-medium ${SLA_STATUS_COLOR[ticket.slaInfo.resolutionStatus]}`}
                  >
                    {ticket.slaInfo.resolutionStatus === 'MET'
                      ? '✓ Respecté'
                      : ticket.slaInfo.resolutionStatus === 'VIOLATED'
                        ? '✗ Expiré'
                        : ticket.slaInfo.resolutionRemainingMs != null
                          ? fmtDuration(ticket.slaInfo.resolutionRemainingMs)
                          : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Commentaires */}
          <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[var(--brand-dark)] mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Commentaires ({comments.length})
            </h3>

            <div className="space-y-4 mb-5">
              {comments.map((c) => (
                <div key={c.id} className={`flex gap-3 ${c.isInternal ? 'opacity-70' : ''}`}>
                  <Avatar user={c.author} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-[var(--brand-dark)]">
                        {c.author.firstName} {c.author.lastName}
                      </span>
                      <span className="text-[10px] text-[var(--tx3)]">{fmtRelTime(c.createdAt)}</span>
                      {c.isInternal && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#FEF3C7] text-[#D97706] rounded">
                          Note interne
                        </span>
                      )}
                    </div>
                    <div
                      className={`p-3 rounded-lg text-sm text-[var(--tx2)] ${c.isInternal ? 'bg-[#FFFBEB] border border-[rgba(245,158,11,0.15)]' : 'bg-[#F4F2FB]'}`}
                    >
                      {c.content}
                    </div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-[var(--tx3)] text-sm text-center py-4">Aucun commentaire</p>
              )}
            </div>

            {/* Zone saisie */}
            <div className="space-y-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Écrire un commentaire…"
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none"
              />
              <div className="flex items-center justify-between">
                {isAgent ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded border-[#D6D2EC] bg-[var(--surface-2)] text-blue-500"
                    />
                    <span className="text-xs text-[var(--tx3)]">Note interne (agents seulement)</span>
                  </label>
                ) : (
                  <span />
                )}
                <button
                  onClick={() => commentMutation.mutate()}
                  disabled={!comment.trim() || commentMutation.isPending}
                  className="flex items-center gap-2 px-4 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {commentMutation.isPending ? <Spinner /> : <Check className="w-4 h-4" />} Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar droite */}
        <div className="space-y-4">
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-4">
            <h3 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider">
              Informations
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-[var(--tx3)] mb-1">Créé par</p>
                <div className="flex items-center gap-2">
                  <Avatar user={ticket.creator} size="xs" />
                  <span className="text-[var(--brand-dark)]">
                    {ticket.creator?.firstName} {ticket.creator?.lastName}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-[var(--tx3)] mb-1">Assigné à</p>
                {ticket.assignee ? (
                  <div className="flex items-center gap-2">
                    <Avatar user={ticket.assignee} size="xs" />
                    <span className="text-[var(--brand-dark)]">
                      {ticket.assignee.firstName} {ticket.assignee.lastName}
                    </span>
                  </div>
                ) : (
                  <button onClick={() => setShowAssign(true)}
                    className="text-xs text-[var(--blue)] hover:underline">
                    + Assigner
                  </button>
                )}
                {ticket.assignee && (
                  <button onClick={() => setShowAssign(true)}
                    className="ml-2 text-xs text-[var(--tx3)] hover:text-[#DC2626]">
                    Changer
                  </button>
                )}
              </div>

              {/* Modal assignation */}
              {showAssign && (
                <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
                  <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-sm p-5 space-y-3"
                    style={{ boxShadow:'0 24px 64px rgba(13,8,32,0.18)' }}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[var(--brand-dark)]">Assigner le ticket</h3>
                      <button onClick={() => setShowAssign(false)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">✕</button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {(agentUsers as any[]).map((u: any) => (
                        <button key={u.id} onClick={() => assignMutation.mutate(u.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors text-left">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] text-[10px] font-bold flex-shrink-0">
                            {u.firstName?.[0]}{u.lastName?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-[var(--brand-dark)]">{u.firstName} {u.lastName}</p>
                            <p className="text-xs text-[var(--tx3)]">{u.email}</p>
                          </div>
                        </button>
                      ))}
                      {(agentUsers as any[]).length === 0 && (
                        <p className="text-xs text-[var(--tx3)] text-center py-4">Chargement…</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {ticket.category && (
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Catégorie</p>
                  <span className="text-[var(--brand-dark)]">{ticket.category.name}</span>
                </div>
              )}
              <div>
                <p className="text-xs text-[var(--tx3)] mb-1">Créé le</p>
                <span className="text-[var(--brand-dark)]">
                  {fmtDate(ticket.createdAt)} à {fmtTime(ticket.createdAt)}
                </span>
              </div>
              {ticket.resolvedAt && (
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Résolu le</p>
                  <span className="text-[var(--brand-dark)]">{fmtDate(ticket.resolvedAt)}</span>
                </div>
              )}
              {ticket.resolutionMinutes && (
                <div>
                  <p className="text-xs text-[var(--tx3)] mb-1">Temps de résolution</p>
                  <span className="text-[var(--brand-dark)]">
                    {fmtDuration(ticket.resolutionMinutes * 60000)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Phase 5-B — Satisfaction */}
        <SatisfactionWidget ticket={ticket} />

        {/* Phase 5-B — Sous-tickets */}
        <SubTicketsPanel ticketId={id!} canCreate={hasPermission('tickets:create')} />

        {/* Phase 5-B — Tickets liés */}
        <LinkedTicketsPanel
          ticketId={id!}
          linkedTickets={(ticket as any).linkedTickets ?? []}
          linkedFrom={(ticket as any).linkedFrom ?? []}
          canEdit={hasPermission('tickets:update')}
        />
      </div>

      {/* Modal résolution */}
      {showResolve && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
          <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl shadow-2xl p-5">
            <h2 className="text-sm font-bold text-[var(--brand-dark)] mb-4">Résoudre le ticket</h2>
            <textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              rows={4}
              placeholder="Description de la solution apportée…"
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResolve(false)}
                className="px-4 py-2 text-sm text-[var(--tx3)]"
              >
                Annuler
              </button>
              <button
                onClick={() => resolveMutation.mutate()}
                disabled={!resolution.trim() || resolveMutation.isPending}
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-lg text-sm disabled:opacity-50"
              >
                {resolveMutation.isPending ? <Spinner /> : <CheckCircle className="w-4 h-4" />}{' '}
                Résoudre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  CRÉER TICKET
// ============================================================================

export function CreateTicketPage() {
  const navigate = useNavigate()
  const addNotif = useNotificationStore((s) => s.addNotification)
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    categoryId: '',
    application: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
  })
  const f = (k: string) => (e: any) => setForm((p) => ({ ...p, [k]: e.target.value }))

  const { data: categories = [] } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: ticketsApi.listCategories,
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      // Champs vides → absents (évite categoryId: '' → FK invalide, et des '' stockés)
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? undefined : v]),
      )
      return ticketsApi.createTicket(payload)
    },
    onSuccess: (data: any) => {
      addNotif({
        type: 'success',
        title: `Ticket #${data.number} créé`,
      })
      navigate(`/tickets/${data.id}`)
    },
    onError: (err: any) =>
      addNotif({
        type: 'error',
        title: 'Création impossible',
        message: err?.response?.data?.message ?? 'Erreur inattendue',
      }),
  })

  const inputCls =
    'w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]'

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/tickets')}
          className="text-[var(--tx3)] hover:text-[var(--brand-dark)] text-sm"
        >
          ← Retour
        </button>
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Nouveau ticket</h1>
      </div>

      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Titre *</label>
          <input
            className={inputCls}
            value={form.title}
            onChange={f('title')}
            placeholder="Décrivez brièvement le problème"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Priorité *</label>
            <select className={inputCls} value={form.priority} onChange={f('priority')}>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Catégorie</label>
            <select className={inputCls} value={form.categoryId} onChange={f('categoryId')}>
              <option value="">Sélectionner…</option>
              {(categories as any[]).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">Description *</label>
          <textarea
            className={inputCls}
            rows={4}
            value={form.description}
            onChange={f('description')}
            placeholder="Décrivez le problème en détail…"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
            Application concernée
          </label>
          <input
            className={inputCls}
            value={form.application}
            onChange={f('application')}
            placeholder="Ex: Module paie, Dashboard RH…"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
            Étapes pour reproduire
          </label>
          <textarea
            className={inputCls}
            rows={3}
            value={form.stepsToReproduce}
            onChange={f('stepsToReproduce')}
            placeholder="1. Aller sur...\n2. Cliquer sur...\n3. Observer..."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Comportement attendu
            </label>
            <textarea
              className={inputCls}
              rows={2}
              value={form.expectedBehavior}
              onChange={f('expectedBehavior')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--tx3)] mb-1.5">
              Comportement observé
            </label>
            <textarea
              className={inputCls}
              rows={2}
              value={form.actualBehavior}
              onChange={f('actualBehavior')}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={() => navigate('/tickets')}
            className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)]"
          >
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={isPending || !form.title || !form.description}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-medium disabled:opacity-50"
          >
            {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer le ticket
          </button>
        </div>
      </div>
    </div>
  )
}


// ============================================================================
//  CATÉGORIES DE TICKETS
// ============================================================================

export function TicketCategoriesPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ name: '', description: '', color: 'var(--brand-orange)' })

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn:  () => ticketsApi.listCategories(),
  })

  const createMutation = useMutation({
    mutationFn: () => ticketsApi.createCategory(form),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['ticket-categories'] })
      setShowCreate(false)
      setForm({ name: '', description: '', color: 'var(--brand-orange)' })
      addNotification({ type: 'success', title: 'Catégorie créée' })
    },
    onError: (e: any) => addNotification({ type: 'error', title: 'Erreur', message: e.response?.data?.message }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ticketsApi.deleteCategory(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['ticket-categories'] }),
  })

  const COLORS = ['var(--brand-orange)','var(--brand-amber)','#059669','#D97706','#DC2626','var(--blue-d)','var(--tx3)']

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Catégories</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Organiser les tickets par catégorie</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-xl text-sm font-semibold transition-colors">
          + Nouvelle catégorie
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (categories as any[]).length === 0 ? (
        <div className="text-center py-16 text-[var(--tx3)]">
          <p className="text-lg font-medium mb-2">Aucune catégorie</p>
          <p className="text-sm">Créez votre première catégorie pour organiser les tickets</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(categories as any[]).map((cat: any) => (
            <div key={cat.id} className="bg-white border border-[var(--bd)] rounded-xl p-4 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${cat.color}20`, border: `1px solid ${cat.color}40` }}>
                  <span style={{ color: cat.color }} className="text-lg">🏷️</span>
                </div>
                <div>
                  <p className="font-semibold text-[var(--brand-dark)] text-sm">{cat.name}</p>
                  {cat.description && <p className="text-xs text-[var(--tx3)] mt-0.5">{cat.description}</p>}
                  <p className="text-xs text-[var(--tx3)] mt-1">{cat._count?.tickets ?? 0} ticket(s)</p>
                </div>
              </div>
              <button onClick={() => { if (window.confirm('Supprimer cette catégorie ?')) deleteMutation.mutate(cat.id) }}
                className="p-1.5 text-[var(--tx3)] hover:text-[#DC2626] transition-colors">
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md p-6 space-y-4"
            style={{ boxShadow:'0 24px 64px rgba(13,8,32,0.18)' }}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[var(--brand-dark)]">Nouvelle catégorie</h3>
              <button onClick={() => setShowCreate(false)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Nom *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="ex: Bug, Support, Facturation..."
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description optionnelle"
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:border-[var(--blue)]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--tx3)] mb-1">Couleur</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`w-8 h-8 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-[var(--brand-orange)] scale-110' : 'hover:scale-110'}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 px-4 py-2 border border-[var(--bd)] rounded-xl text-sm text-[var(--tx3)]">Annuler</button>
              <button onClick={() => createMutation.mutate()}
                disabled={!form.name || createMutation.isPending}
                className="flex-1 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white rounded-xl text-sm font-semibold">
                {createMutation.isPending ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  SLA
// ============================================================================

export function TicketSlaPage() {
  const { data: slaData } = useQuery({
    queryKey: ['ticket-sla'],
    queryFn:  () => ticketsApi.getSlaStats?.() ?? Promise.resolve(null),
  })

  const SLA_POLICIES = [
    { priority: 'URGENT', label: 'Urgent',  color: '#DC2626', response: '1h',  resolution: '4h'  },
    { priority: 'HIGH',   label: 'Élevé',   color: '#D97706', response: '4h',  resolution: '24h' },
    { priority: 'MEDIUM', label: 'Moyen',   color: 'var(--blue-d)', response: '8h',  resolution: '48h' },
    { priority: 'LOW',    label: 'Faible',  color: 'var(--tx3)', response: '24h', resolution: '72h' },
  ]

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--brand-dark)]">SLA — Accords de niveau de service</h1>
        <p className="text-sm text-[var(--tx3)] mt-0.5">Délais de réponse et résolution par priorité</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SLA_POLICIES.map(p => (
          <div key={p.priority} className="bg-white border border-[var(--bd)] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 rounded-full" style={{ background: p.color }} />
              <span className="font-semibold text-[var(--brand-dark)]">{p.label}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[var(--surface-2)] rounded-lg p-3">
                <p className="text-xs text-[var(--tx3)] mb-1">Première réponse</p>
                <p className="text-lg font-bold" style={{ color: p.color }}>{p.response}</p>
              </div>
              <div className="bg-[var(--surface-2)] rounded-lg p-3">
                <p className="text-xs text-[var(--tx3)] mb-1">Résolution</p>
                <p className="text-lg font-bold" style={{ color: p.color }}>{p.resolution}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4">
        <p className="text-sm text-[var(--tx3)]">
          💡 Les SLA sont calculés sur les heures ouvrées (8h-18h, lundi-vendredi). 
          Les violations SLA sont remontées en temps réel dans le dashboard tickets.
        </p>
      </div>
    </div>
  )
}
